import dayjs from "dayjs";
import { createETaxNoteBatch } from "../../../services";
import { ETaxCreateBatchResult, ETaxNote } from "../../../types/e-tax.type";
import { reportSendError } from "../reportSendError";
import { SendProblem } from "../SendProblemModal";
import {
  SEND_CHUNK_SIZE,
  SendResultSummary,
  chunk,
  extractBatchRefusals,
  mergeBatchResults,
} from "../sendBatch";
import { toNoteBatchItem } from "./noteSend";

const NOTE_BATCH_NAME_PREFIX = "E-Tax CN/DN";
const BATCH_NAME_STAMP_FORMAT = "YYYY-MM-DD HH:mm";

export type NoteSendOutcome =
  /** Every chunk was accepted. `result` is null when an older API answered without counts. */
  | { kind: "SENT"; result: SendResultSummary | null }
  /**
   * The first chunk failed for a reason other than a gate refusal. Nothing was written and the
   * error toast has been shown, so the confirmation stays open for another try.
   */
  | { kind: "RETRY" }
  /**
   * A gate refusal (it rejects its whole chunk), or a later chunk failed after earlier ones were
   * written. `leavingUuids` are the notes that must leave the selection: written or refused.
   */
  | { kind: "STOPPED"; leavingUuids: string[]; problem: SendProblem };

const noteBatchName = (stamp: string, index: number, chunkCount: number): string =>
  chunkCount > 1
    ? `${NOTE_BATCH_NAME_PREFIX} ${stamp} (${index + 1}/${chunkCount})`
    : `${NOTE_BATCH_NAME_PREFIX} ${stamp}`;

const toStoppedOutcome = (
  error: unknown,
  acceptedUuids: string[],
  notSentCount: number,
): NoteSendOutcome => {
  const refusals = extractBatchRefusals(error);
  if (!refusals) reportSendError(error);
  if (!refusals && acceptedUuids.length === 0) return { kind: "RETRY" };
  return {
    kind: "STOPPED",
    leavingUuids: [...acceptedUuids, ...(refusals ?? []).map((row) => row.transaction_uuid)],
    problem: { refusals: refusals ?? [], alreadySentCount: acceptedUuids.length, notSentCount },
  };
};

/**
 * Sends the notes to e-Tax, in chunks the API accepts, and says how far it got. Chunks before a
 * failed one are already written, so the caller must drop them from the selection and report them.
 */
export const sendNoteBatches = async (notes: ETaxNote[]): Promise<NoteSendOutcome> => {
  const stamp = dayjs().format(BATCH_NAME_STAMP_FORMAT);
  const chunks = chunk(notes, SEND_CHUNK_SIZE);
  const results: Array<ETaxCreateBatchResult | null> = [];
  const acceptedUuids: string[] = [];

  for (let index = 0; index < chunks.length; index += 1) {
    try {
      const response = await createETaxNoteBatch({
        batch_name: noteBatchName(stamp, index, chunks.length),
        items: chunks[index].map(toNoteBatchItem),
      });
      // An older backend answers `data: null`; the result dialog then only says "สำเร็จ".
      results.push(response.data?.data ?? null);
    } catch (error) {
      return toStoppedOutcome(error, acceptedUuids, notes.length - acceptedUuids.length);
    }
    chunks[index].forEach((note) => acceptedUuids.push(note.transaction_uuid));
  }

  return { kind: "SENT", result: mergeBatchResults(results) };
};
